import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Loader2, Check, X, AlertTriangle, PackageSearch, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import OfflineFeatureGate from '../components/OfflineFeatureGate';

export default function ExceptionQueuePage() {
  return (
    <OfflineFeatureGate feature="Exception Queue">
      <ExceptionQueueContent />
    </OfflineFeatureGate>
  );
}

function ExceptionQueueContent() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [resolving, setResolving] = useState(null);
  const [approving, setApproving] = useState(null);
  const [subcategories, setSubcategories] = useState([]);

  useEffect(() => {
    api.get('/product-taxonomy/categories').then((res) => {
      const subs = [];
      (res.data?.categories || []).forEach((c) => (c.subcategories || []).forEach((sc) => {
        subs.push({
          id: sc.id,
          label: `${c.name} › ${sc.name}${sc.code_min == null ? ' (no code range)' : ''}`,
          disabled: sc.code_min == null,
        });
      }));
      setSubcategories(subs);
    }).catch(() => {});
  }, []);

  const loadQueue = useCallback(async () => {
    try {
      const res = await api.get(`/stocktake/exception-queue?status=${statusFilter}`);
      setData(res.data);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error('Access denied — Admin or Senior Scheduler role required');
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  const handleApprove = async (requestId, notes, subcategoryId) => {
    setApproving(requestId);
    try {
      const res = await api.post(`/stocktake/product-requests/${requestId}/approve`, {
        notes: notes || null,
        subcategory_id: subcategoryId ? Number(subcategoryId) : null,
      });
      toast.success(`Approved — created "${res.data.product_name}" (code ${res.data.product_code || '—'})`);
      loadQueue();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Approve failed');
    } finally {
      setApproving(null);
    }
  };

  const handleReject = async (requestId, notes) => {
    try {
      await api.post(`/stocktake/product-requests/${requestId}/reject`, { notes: notes || null });
      toast.info('Request rejected');
      loadQueue();
    } catch (e) {
      toast.error('Reject failed');
    }
  };

  const handleResolveUnmatched = async (itemId, productId) => {
    setResolving(itemId);
    try {
      await api.post(`/stocktake/unmatched-items/${itemId}/resolve`, { product_id: productId });
      toast.success('Unmatched item resolved');
      loadQueue();
    } catch (e) {
      toast.error('Resolve failed');
    } finally {
      setResolving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const { unmatched_items = [], product_requests = [], summary = {} } = data || {};

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="exception-queue-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="exception-queue-title">Exception Queue</h1>
          <p className="text-sm text-slate-500 mt-1">
            {summary.pending_unmatched || 0} unmatched items, {summary.pending_requests || 0} product requests pending
          </p>
        </div>
        <div className="flex rounded-lg border border-slate-200 overflow-hidden">
          {['pending', 'all', 'resolved', 'rejected'].map(s => (
            <button
              key={s}
              onClick={() => { setStatusFilter(s); setLoading(true); }}
              className={`px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
                statusFilter === s ? 'bg-slate-900 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
              }`}
              data-testid={`queue-filter-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Product Requests */}
      {product_requests.length > 0 && (
        <section className="mb-8">
          <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <PackageSearch className="w-5 h-5" />
            Product Requests
          </h2>
          <div className="space-y-3">
            {product_requests.map(req => (
              <Card key={req.id} className="p-4" data-testid={`product-request-${req.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-slate-900">{req.product_name}</span>
                      <StatusBadge status={req.status} />
                    </div>
                    <div className="text-sm text-slate-500 space-y-0.5">
                      {req.suggested_category && <p>Category: {req.suggested_category}</p>}
                      {req.unit_label && <p>Unit: {req.unit_label}</p>}
                      {req.notes && <p>Notes: {req.notes}</p>}
                      <p className="text-xs text-slate-400">
                        Session #{req.session_id} &middot; Site #{req.site_id} &middot; {req.created_at?.split('T')[0]}
                      </p>
                    </div>
                  </div>
                  {req.status === 'pending' && (
                    <ReviewActionControl
                      id={req.id}
                      approving={approving}
                      subcategories={subcategories}
                      onApprove={handleApprove}
                      onReject={handleReject}
                    />
                  )}
                  {req.status !== 'pending' && req.reviewer_notes && (
                    <p className="text-xs text-slate-500 italic mt-1">Review notes: {req.reviewer_notes}</p>
                  )}
                  {req.status === 'approved' && req.created_product_id && (
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      Product #{req.created_product_id}
                    </Badge>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Unmatched Items */}
      {unmatched_items.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <ClipboardList className="w-5 h-5" />
            Unmatched Items
          </h2>
          <div className="space-y-3">
            {unmatched_items.map(item => (
              <Card key={item.id} className="p-4" data-testid={`unmatched-queue-item-${item.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-slate-900">{item.product_name}</span>
                      <StatusBadge status={item.status} />
                      <Badge variant="outline" className="text-[10px]">{item.item_type}</Badge>
                    </div>
                    <div className="text-sm text-slate-500 space-y-0.5">
                      {item.quantity != null && <p>Quantity: {item.quantity} {item.unit_label || ''}</p>}
                      {item.cost_price != null && <p>Cost: {item.cost_price}</p>}
                      {item.notes && <p>Notes: {item.notes}</p>}
                      <p className="text-xs text-slate-400">
                        Session #{item.session_id} &middot; Site #{item.site_id} &middot; {item.created_at?.split('T')[0]}
                      </p>
                    </div>
                  </div>
                  {item.status === 'pending' && (
                    <ResolveControl
                      itemId={item.id}
                      resolving={resolving}
                      onResolve={handleResolveUnmatched}
                    />
                  )}
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Empty state */}
      {product_requests.length === 0 && unmatched_items.length === 0 && (
        <div className="text-center py-20 text-slate-400" data-testid="exception-queue-empty">
          <Check className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="text-base">No {statusFilter} exceptions</p>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    resolved: 'bg-green-50 text-green-700 border-green-200',
    approved: 'bg-green-50 text-green-700 border-green-200',
    rejected: 'bg-red-50 text-red-700 border-red-200',
  };
  return (
    <Badge variant="outline" className={`text-[10px] ${styles[status] || ''}`}>
      {status}
    </Badge>
  );
}

function ResolveControl({ itemId, resolving, onResolve }) {
  const [productId, setProductId] = useState('');

  return (
    <div className="flex items-center gap-2 shrink-0">
      <Input
        type="number"
        placeholder="Product ID"
        value={productId}
        onChange={(e) => setProductId(e.target.value)}
        className="w-24 h-8 text-xs"
        data-testid={`resolve-product-id-${itemId}`}
      />
      <Button
        size="sm"
        onClick={() => productId && onResolve(itemId, parseInt(productId, 10))}
        disabled={!productId || resolving === itemId}
        data-testid={`resolve-btn-${itemId}`}
      >
        {resolving === itemId ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Resolve'}
      </Button>
    </div>
  );
}

function ReviewActionControl({ id, approving, subcategories, onApprove, onReject }) {
  const [notes, setNotes] = useState('');
  const [subcategoryId, setSubcategoryId] = useState('');
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="shrink-0">
      {expanded ? (
        <div className="space-y-2">
          <select
            value={subcategoryId}
            onChange={(e) => setSubcategoryId(e.target.value)}
            className="block w-48 text-xs border border-slate-200 rounded-lg p-2 bg-white"
            data-testid={`review-subcategory-${id}`}
          >
            <option value="">Subcategory (required)…</option>
            {subcategories.map((sc) => (
              <option key={sc.id} value={sc.id} disabled={sc.disabled}>{sc.label}</option>
            ))}
          </select>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Review notes (optional)..."
            rows={2}
            className="block w-48 text-xs border border-slate-200 rounded-lg p-2 resize-none"
            data-testid={`review-notes-${id}`}
          />
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              onClick={() => onApprove(id, notes, subcategoryId)}
              disabled={approving === id || !subcategoryId}
              data-testid={`approve-request-${id}`}
            >
              {approving === id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
              Approve
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-red-500 hover:text-red-700"
              onClick={() => onReject(id, notes)}
              data-testid={`reject-request-${id}`}
            >
              <X className="w-3 h-3 mr-1" /> Reject
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            onClick={() => setExpanded(true)}
            data-testid={`review-expand-${id}`}
          >
            Review
          </Button>
        </div>
      )}
    </div>
  );
}
